import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Eye,
  EyeOff,
  Lock,
  Shield,
} from "lucide-react";
import React, { memo, useCallback, useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { authService } from "../services/api";

// Composant InputField mémorisé pour éviter les re-renders
const InputField = memo(
  ({
    name,
    label,
    type,
    placeholder,
    required = false,
    value,
    onChange,
    error,
    showPassword,
    setShowPassword,
  }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Lock className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`block w-full pl-10 pr-10 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
            error
              ? "border-red-300 bg-red-50"
              : "border-gray-300 hover:border-gray-400"
          }`}
        />
        <button
          type="button"
          onClick={setShowPassword}
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
        >
          {showPassword ? (
            <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
          ) : (
            <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
          )}
        </button>
      </div>
      {error && (
        <div className="flex items-center space-x-1 text-red-600 text-sm">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  )
);

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    new_password: "",
    confirm_password: "",
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.new_password) {
      newErrors.new_password = "Le nouveau mot de passe est requis";
    } else if (formData.new_password.length < 6) {
      newErrors.new_password =
        "Le mot de passe doit contenir au moins 6 caractères";
    }

    if (!formData.confirm_password) {
      newErrors.confirm_password =
        "La confirmation du mot de passe est requise";
    } else if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = "Les mots de passe ne correspondent pas";
    }

    // S'assurer que toutes les erreurs sont des chaînes
    const safeErrors = {};
    Object.keys(newErrors).forEach((key) => {
      const error = newErrors[key];
      safeErrors[key] =
        typeof error === "string" ? error : "Erreur de validation";
    });

    setErrors(safeErrors);
    return Object.keys(safeErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      toast.error("Token de réinitialisation manquant");
      return;
    }

    if (!validateForm()) {
      toast.error("Veuillez corriger les erreurs dans le formulaire");
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword(token, formData.new_password);

      toast.success("Mot de passe réinitialisé avec succès !");
      setSuccess(true);

      // Rediriger vers la page de connexion après 2 secondes
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error) {
      console.error("Reset password error:", error);

      // Gérer les erreurs de validation
      let errorMessage = "Erreur lors de la réinitialisation du mot de passe";

      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;

        // Si c'est une chaîne, l'utiliser directement
        if (typeof detail === "string") {
          errorMessage = detail;
        }
        // Si c'est un objet d'erreur de validation, extraire le message
        else if (typeof detail === "object" && detail.msg) {
          errorMessage = detail.msg;
        }
        // Si c'est un tableau d'erreurs, prendre la première
        else if (Array.isArray(detail) && detail.length > 0) {
          const firstError = detail[0];
          errorMessage =
            typeof firstError === "string"
              ? firstError
              : firstError.msg || errorMessage;
        }
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Clear error when user starts typing
      if (errors[name]) {
        setErrors((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    },
    [errors]
  );

  const toggleShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const toggleShowConfirmPassword = useCallback(() => {
    setShowConfirmPassword((prev) => !prev);
  }, []);

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-red-500 to-pink-500 text-white">
                <AlertCircle className="h-8 w-8" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Token manquant
            </h2>
            <p className="text-gray-600 mb-6">
              Le lien de réinitialisation est invalide ou manquant.
            </p>
            <Link
              to="/forgot-password"
              className="inline-flex items-center text-blue-600 hover:text-blue-500 font-medium"
            >
              Demander un nouveau lien
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white">
                <CheckCircle className="h-8 w-8" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Mot de passe réinitialisé !
            </h2>
            <p className="text-gray-600 mb-6">
              Votre mot de passe a été réinitialisé avec succès. Vous allez être
              redirigé vers la page de connexion.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center text-blue-600 hover:text-blue-500 font-medium"
            >
              Se connecter maintenant
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white">
              <Shield className="h-8 w-8" />
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Nouveau mot de passe
          </h2>
          <p className="text-gray-600">
            Entrez votre nouveau mot de passe pour finaliser la
            réinitialisation.
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            <InputField
              name="new_password"
              label="Nouveau mot de passe"
              type={showPassword ? "text" : "password"}
              placeholder="Votre nouveau mot de passe"
              required
              value={formData.new_password}
              onChange={handleChange}
              error={errors.new_password}
              showPassword={showPassword}
              setShowPassword={toggleShowPassword}
            />

            <InputField
              name="confirm_password"
              label="Confirmer le nouveau mot de passe"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirmez votre nouveau mot de passe"
              required
              value={formData.confirm_password}
              onChange={handleChange}
              error={errors.confirm_password}
              showPassword={showConfirmPassword}
              setShowPassword={toggleShowConfirmPassword}
            />

            {/* Password Requirements */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">
                Exigences du mot de passe :
              </h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Au moins 6 caractères
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Facile à retenir mais sécurisé
                </li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5 transition-all duration-200"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Réinitialisation en cours...
                </>
              ) : (
                <>
                  <Shield className="h-4 w-4 mr-2" />
                  Réinitialiser mon mot de passe
                </>
              )}
            </button>
          </form>
        </div>

        {/* Back to Login */}
        <div className="text-center">
          <Link
            to="/login"
            className="inline-flex items-center text-blue-600 hover:text-blue-500 font-medium"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour à la connexion
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
